import type { UserReturnModel, UserProfileUpdateModel, OrganizationUpdateModel } from 'src/lib/api';

import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import { api, endpoints } from 'src/lib/api';

import { toast } from 'src/components/snackbar';

import { useHRAuth } from 'src/auth/hooks/use-hr-context';

export interface OrganisationDetails {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  profilePictureUrl: string;
  email: string;
  emailConfirmed: boolean;
  organizationId: number | string;
  organizationName: string;
  organizationSlug: string;
  organizationLogo: string;
  organizationWebsite: string;
}

type OnSettled = (success: boolean) => void;

interface OrganisationContextType extends OrganisationDetails {
  updateProfile: (data: UserProfileUpdateModel, onSettled?: OnSettled) => void;
  updateProfilePicture: (file: File, onSettled?: OnSettled) => void;
  updateOrganisation: (data: OrganizationUpdateModel, onSettled?: OnSettled) => void;
  updateOrganisationLogo: (file: File, onSettled?: OnSettled) => void;
  deleteOrganisationLogo: (onSettled?: OnSettled) => void;
  deleteProfilePicture: (onSettled?: OnSettled) => void;
}

const OrganisationContext = createContext<OrganisationContextType | null>(null);

const EMPTY_ORGANISATION: OrganisationDetails = {
  id: '',
  firstName: '',
  lastName: '',
  displayName: '',
  profilePictureUrl: '',
  email: '',
  emailConfirmed: false,
  organizationId: '',
  organizationName: '',
  organizationSlug: '',
  organizationLogo: '',
  organizationWebsite: '',
};

const mapApiResponseToOrganisation = (user: UserReturnModel | null): OrganisationDetails => {
  if (!user) return EMPTY_ORGANISATION;

  const [organization] = user.Organizations;

  return {
    id: user.Id,
    firstName: user.FirstName,
    lastName: user.LastName,
    displayName: user.DisplayName,
    profilePictureUrl: user.ProfilePictureUrl ?? '',
    email: user.Email,
    emailConfirmed: user.EmailConfirmed,
    organizationId: organization?.OrganizationId ?? '',
    organizationName: organization?.Name ?? '',
    organizationSlug: organization?.Slug ?? '',
    organizationLogo: organization?.Logo ?? '',
    organizationWebsite: organization?.Url ?? '',
  };
};

const organizationPathOf = (organisation: OrganisationDetails | null) => ({
  path: { id: Number(organisation?.organizationId) },
});

const withPatch = (patch: Partial<OrganisationDetails>) => (prev: OrganisationDetails | null) =>
  prev && { ...prev, ...patch };

type ToastMessages = { success: string; failure: string };

async function settle<T>(
  request: Promise<T>,
  apply: (response: T) => void,
  { success, failure }: ToastMessages,
  onSettled?: OnSettled
) {
  try {
    apply(await request);
    toast.success('Success', { description: success });
    onSettled?.(true);
  } catch {
    toast.error('Failed', { description: failure });
    onSettled?.(false);
  }
}

export const OrganisationProvider = ({ children }: { children: React.ReactNode }) => {
  const [organisation, setOrganisation] = useState<OrganisationDetails | null>(null);
  const { Token } = useHRAuth();

  const fetchOrganisation = async () => {
    try {
      const res = await api(endpoints.account.getUser);
      setOrganisation(mapApiResponseToOrganisation(res));
    } catch {
      setOrganisation(null);
    }
  };

  useEffect(() => {
    if (Token) {
      fetchOrganisation();
    }
  }, [Token]);

  useEffect(() => {
    if (!organisation) return;

    datadogRum.setUser({
      id: organisation.id,
      name: organisation.displayName,
      email: organisation.email,
    });

    datadogRum.setGlobalContextProperty('organizationId', organisation.organizationId);
    datadogRum.setGlobalContextProperty('organizationSlug', organisation.organizationSlug);

    datadogLogs.setGlobalContextProperty('organizationId', organisation.organizationId);
    datadogLogs.setGlobalContextProperty('organizationSlug', organisation.organizationSlug);
  }, [organisation]);

  const updateProfile = useCallback(
    (data: UserProfileUpdateModel, onSettled?: OnSettled) =>
      settle(
        api(endpoints.account.updateUser, { body: data }),
        (user) => setOrganisation(mapApiResponseToOrganisation(user)),
        {
          success: 'Profile updated successfully.',
          failure: 'Failed to update profile details.',
        },
        onSettled
      ),
    []
  );

  const updateProfilePicture = useCallback(
    (file: File, onSettled?: OnSettled) =>
      settle(
        api(endpoints.account.uploadProfilePicture, { body: { file } }),
        (user) => setOrganisation(mapApiResponseToOrganisation(user)),
        {
          success: 'Profile picture updated successfully.',
          failure: 'Failed to update profile picture.',
        },
        onSettled
      ),
    []
  );

  const deleteProfilePicture = useCallback(
    (onSettled?: OnSettled) =>
      settle(
        api(endpoints.account.deleteProfilePicture),
        () => setOrganisation(withPatch({ profilePictureUrl: '' })),
        {
          success: 'Profile picture deleted successfully.',
          failure: 'Something went wrong. Please try again later.',
        },
        onSettled
      ),
    []
  );

  const updateOrganisation = useCallback(
    (data: OrganizationUpdateModel, onSettled?: OnSettled) =>
      settle(
        api(endpoints.organizations.update, {
          params: organizationPathOf(organisation),
          body: data,
        }),
        (org) =>
          setOrganisation(
            withPatch({
              organizationName: org.Name,
              organizationSlug: org.Slug,
              organizationWebsite: org.Url ?? '',
            })
          ),
        {
          success: 'Organisation updated successfully.',
          failure: 'Failed to update organisation.',
        },
        onSettled
      ),
    [organisation]
  );

  const deleteOrganisationLogo = useCallback(
    (onSettled?: OnSettled) =>
      settle(
        api(endpoints.organizations.deleteOrganizationLogo, {
          params: organizationPathOf(organisation),
        }),
        () => setOrganisation(withPatch({ organizationLogo: '' })),
        {
          success: 'Organisation logo deleted successfully.',
          failure: 'Something went wrong. Please try again later.',
        },
        onSettled
      ),
    [organisation]
  );

  const updateOrganisationLogo = useCallback(
    (file: File, onSettled?: OnSettled) =>
      settle(
        api(endpoints.organizations.uploadOrganizationLogo, {
          params: organizationPathOf(organisation),
          body: { file },
        }),
        (org) =>
          setOrganisation(
            withPatch({
              organizationName: org.Name,
              organizationSlug: org.Slug,
              organizationLogo: org.Logo ?? '',
            })
          ),
        {
          success: 'Organisation logo updated successfully.',
          failure: 'Failed to update organisation logo.',
        },
        onSettled
      ),
    [organisation]
  );

  const memoizedValue = useMemo<OrganisationContextType>(
    () => ({
      ...(organisation ?? EMPTY_ORGANISATION),
      updateProfile,
      updateProfilePicture,
      updateOrganisation,
      updateOrganisationLogo,
      deleteOrganisationLogo,
      deleteProfilePicture,
    }),
    [
      organisation,
      updateProfile,
      updateProfilePicture,
      updateOrganisation,
      updateOrganisationLogo,
      deleteOrganisationLogo,
      deleteProfilePicture,
    ]
  );

  return (
    <OrganisationContext.Provider value={memoizedValue}>{children}</OrganisationContext.Provider>
  );
};

export const useOrganisation = () => {
  const context = useContext(OrganisationContext);
  if (!context) {
    throw new Error('useOrganisation must be used within an OrganisationProvider');
  }
  return context;
};
